import { ReactSortable } from 'react-sortablejs';
import {useSafeState} from "ahooks";
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { DimensionMapInfo, CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import {checkNullString} from '@/pages/datalab/utils/utils';
import React, { useState, useMemo, useCallback } from 'react';
import { initDragStatus } from '@/pages/datalab/components/SortableDrag/utils';
import { createShowDimensionSortablePersist } from '@/pages/datalab/components/SortableDrag/utils/showDimensionSortablePersist';
import { TDispatchFun, SortableItems } from '@/pages/datalab/components/SortableDrag/configration';
import { SortableType } from '@dbl/core/chartEditor';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { SortableAffix } from '@/pages/datalab/components/SortableDrag/Sortable/sortableAffix';
import {DropdownIcon} from "@/pages/datalab/components/SortableDrag/Sortable/dropdownIcon";
import DimensionMapSetModal from "@/pages/datalab/components/SortableDrag/Sortable/DimensionMapSetModal";
import {
  duplicateNameVerification,
  RenameWrapper
} from "@/pages/datalab/components/SortableDrag/FieldEditComponents";
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import {checkConditions} from "@/pages/datalab/pages/Multidimensional/utils";
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";

export interface DimensionSortableProps {
  sortableKey: string;
  sortableList: IColumnInfo[];
  sortableOption: ISortableOption;
  dispatchFun: TDispatchFun;
  dimensionMap: DimensionMapInfo;
  isEn: boolean;
  dataTables: IGetTableInfoDto[];
  chartType: string;
  filters: any[];
  showCustomColumnModel: (column: IColumnInfo) => void;
  dataTableJoinRelations: ITableJoinRelation[];
  customColumns?: CustomColumn[];
}
export const ShowDimensionSortable = (props: DimensionSortableProps) => {
  const {
    sortableKey,
    sortableList,
    sortableOption,
    dispatchFun,
    dimensionMap,
    isEn,
    dataTables,
    chartType,
    filters,
    customColumns,
  } = props;
  const { dragState, dragMoveHandle } = sortableOption;
  const [currentDragItem, setCurrentDragItem] = useState<IColumnInfo>();
  const [renameSelectComp, setRenameSelectComp] = useSafeState<string>('');
  const [modalInfo, setModalInfo] = useState<{isVisible: boolean; currentDrag: IColumnInfo[] | null;}>({
    isVisible: false,
    currentDrag: null,
  });

  /** 展示维度区写回逻辑单例：close / 拖拽落库 / 批量下拉增删共用同一套副作用 */
  const showDimensionPersist = useMemo(
    () =>
      createShowDimensionSortablePersist({
        sortableKey,
        chartType,
        filters,
        dimensionMap,
        dispatchFun,
      }),
    [sortableKey, chartType, filters, dimensionMap, dispatchFun],
  );

  const handleAfterShowDimensionSave = useCallback((nextList: IColumnInfo[]): void => {
    if (nextList.length > 0) {
      setModalInfo({ isVisible: true, currentDrag: nextList });
    }
  }, []);

  const {
    errorDrag: { [sortableKey]: errorDragType },
    currentDrag,
  } = dragState;


  const updateHandle = (column: IColumnInfo, updateColumn: any) => {
    dispatchFun(
      'updateTableField',
      {
        table_id: column.table_id,
        isEn,
        lan: isEn ? 'en' : 'zh',
        column,
        updateColumn,
      },
    );
  };

  const {dragSuffixText, disabledDrag, disabledError } = useMemo(() => {
    const {showDimension} = ChartTypeInfos[chartType] || {};
    const dimensionList = sortableList.slice(0, -2);
    const {isCheckSuccess, info, disabledError} = checkConditions(showDimension, dimensionList, isEn);
    const disabledDrag = (showDimension?.operator === '===') && sortableList.length - 2 === showDimension.count;
    // 对字段类型的限制
    
    return {
      dragSuffixText: isCheckSuccess ? '' : info,
      disabledDrag,
      disabledError: disabledDrag && currentDrag ? disabledError : '',
    };
  }, [chartType, isEn, sortableList, currentDrag]);

  const batchAddConfig = {
    enabled: sortableKey === SortableType.ShowDimension,
    targetKey: 'columnShowDimensions' as const,
    dataTables,
    customColumns: customColumns || [],
    existingColumns: sortableList.filter(item => !checkNullString(item.id)),
    dispatchFun,
    chartType,
    isEn,
    filters,
    showDimensionPersist,
    onAfterBatchApply: handleAfterShowDimensionSave,
  };

  return (
    <>
      <ReactSortable
        group="groupName"
        animation={100}
        filter={'.data-label-disabled'}
        handle={'.data-label-tips'}
        ghostClass="data-label-tips-orange"
        delay={0}
        list={sortableList}
        forceFallback={true}
        className={`form-data-label-tips showDimensions-content ${sortableKey}-${SortableItems} ${
          dragState.currentDrag === sortableKey ? 'dimensions-acitve' : ''
        }`}
        disabled={!!renameSelectComp}
        setList={(list) => {
          if (
            (dragState.currentDrag === sortableKey &&
              !checkNullString(errorDragType)) ||
            disabledDrag
          ) {
            return false;
          }
          if (JSON.stringify(sortableList) !== JSON.stringify(list)) {
            const nextList = list.filter(item => !checkNullString(item.id));
            const ok = showDimensionPersist.saveList(list);
            if (ok) {
              handleAfterShowDimensionSave(nextList);
            }
            return ok ? undefined : false;
          }
          return undefined;
        }}
        onMove={(evt: any, originalEvent: any) => {
          if (currentDragItem) {
            return dragMoveHandle(
              evt,
              originalEvent,
              dispatchFun,
              currentDragItem,
            );
          }
          return false;
        }}
        onEnd={() => {
          initDragStatus(dispatchFun);
        }}
        onStart={(evt) => {
          setCurrentDragItem(sortableList[evt.oldIndex || 0]);
        }}
      >
        {sortableList.map(
          (item: IColumnInfo, index) =>
            item.id ? (
              <RenameWrapper
                setRenameSelectComp={setRenameSelectComp}
                key={index}
                className={'orange'}
                isEn={isEn}
                oldName={item.column_display}
                changeRename={(name: string) => {
                  const columns =
                    dataTables?.find(
                      ({ table_id }) => table_id === item.table_id,
                    )?.column_list || [];
                  if (duplicateNameVerification(columns, name, isEn)) {
                    return isEn ? 'Duplicate naming' : '重复命名';
                  }
                  updateHandle(item, { display_name: name });
                }}
                renderItem={() => (
                  <div
                    className={`data-label-tips orange`} key={item.id + item.table_code}>
                    <div className="dt-multiple-dropdown-group">
                      <div
                        className="tea-dropdown__value dt-multiple-dropdown-text"
                        style={{ paddingRight: '6px' }}
                      >
                        {`${item.column_display || item.column_name}`}{' '}
                      </div>{' '}
                      <DropdownIcon
                        iconType="pencil-m"
                        className="selectOther-other-icon"
                        onClick={(e) => {
                          e.preventDefault(); 
                          setModalInfo({isVisible: true, currentDrag: null});
                        }}
                      />
                    </div>
                    <div
                      className="close-icon"
                      onClick={() => {
                        showDimensionPersist.removeItems(sortableList, [item]);
                      }}
                    >
                      <i className="selectOther-flex-icon">
                        <SvgIcon type="clonse" />
                      </i>
                    </div>
                  </div>
                )}
              />
            ) : (
              <SortableAffix
                key={index}
                item={item}
                dragState={dragState}
                sortableKey={sortableKey}
                dragSuffixText={dragSuffixText}
                disabledDrag={disabledDrag}
                disabledError={disabledError}
                batchAddConfig={batchAddConfig}
              />
            ),
        )}
      </ReactSortable>
      {modalInfo.isVisible && <DimensionMapSetModal
        modalInfo={modalInfo}
        onClose={() => {
          setModalInfo({isVisible: false, currentDrag: null});
        }}
        dispatchFun={dispatchFun}
        dimensionMap={dimensionMap}
      />}
    </>
  );
};
